// NaviPage
// ========
//
// Wave 14bv: Der neue „NAVI"-Hub — Einstieg für alles Routenbezogene. Ersetzt
// den alten direkten /trails-Tab in der BottomBar. Der Tab heisst jetzt „NAVI"
// und führt hierher; von hier aus wählt der User zwischen den vier
// Teilfunktionen.
//
// Bewusste Farbwahl: SKY (#0ea5e9), klar abgesetzt vom Orange-Accent der
// restlichen App. Orange ist unsere „Action/Ride"-Farbe; Sky ist die
// „Planning/Thinking"-Farbe. Der User soll merken, dass er gerade plant,
// nicht fährt.
//
// Die Kacheln (Wave 14bv Stage 6j — von 4 auf 5 erweitert):
//   1. Trails           → /trails  (bestehende Liste, unverändert)
//   2. Create custom    → /navi/custom  (Finger-Zeichnen + Pin-Mode)
//   3. Custom trails    → /navi/custom-trails  (User-erzeugte, device-local)
//   4. DH / Shuttle     → /runs  (DH-Run-Liste, ehemals eigener Tab)
//   5. All Day Turbo    → placeholder, disabled  (wird ein Signature-Loop-
//                         Builder — Details folgen in einer späteren Wave)
//
// Für die beiden disabled-Kacheln vergeben wir keine Route. Stattdessen wird
// die Kachel als `<div>` gerendert mit gedämpften Farben + Beschriftung
// „Coming soon" — kein Navigationsversuch, kein 404.

import { Link } from 'react-router-dom';
import { type ReactNode } from 'react';
import { ProBadge } from '../components/ProBadge';

export function NaviPage() {
  return (
    <div className="absolute inset-0 overflow-y-auto bg-turbo-900 safe-top">
      <div className="p-5 pb-10">
        <Header />
        <div className="grid grid-cols-2 gap-3">
          <NaviTile
            to="/navi/custom"
            icon={<IconPen />}
            title="Create Custom Tour"
            description="Draw a tour with your finger"
            pro
          />
          <NaviTile
            to="/navi/custom-trails"
            icon={<IconBookmark />}
            title="My Custom Tours"
            description="Your saved tours"
            pro
          />
          <NaviTile
            to="/navi/router"
            icon={<IconRoute />}
            title="Route berechnen"
            description="Point-to-point: shortest, fastest, flattest"
            pro
          />
          {/* Corridor-Selfservice (docs/corridor-selfservice-spec.md): GPX
              hochladen → Server baut das komplette Offline-Paket (Karte,
              Contours, Hillshade, Routing, POIs) → Download hier. */}
          <NaviTile
            to="/navi/corridors"
            icon={<IconCorridor />}
            title="Corridor Downloads"
            description="GPX hochladen → Offline-Paket bauen lassen"
            pro
          />
        </div>
      </div>
    </div>
  );
}

function Header() {
  // Wave 14ca-fix40: Description-Zeile entfernt (Platzverschwendung), nur die
  // grosse NAVI-Headline bleibt. User-Wunsch: "die 2 zeilen unter dem wort
  // NAVI 'plan your ride...' weg damit. platz verschwendung."
  return (
    <div className="mb-5">
      <h1 className="text-3xl font-bold tracking-tight text-sky-400">NAVI</h1>
    </div>
  );
}

/**
 * NaviTile — eine der vier Kacheln im 2×2-Grid.
 *
 * Zwei Modi:
 *   - Aktive Kachel (hat `to`): `<Link>` mit sky-farbiger Umrandung, hover-
 *     state, aktiver Tap-Animation.
 *   - Disabled Kachel (kein `to`): `<div>` mit 40%-Opazität, kein Cursor,
 *     „Coming soon"-Sub-Text.
 *
 * Die Höhe ist bewusst üppig (min-h-[9.5rem]) damit die Kacheln auf iPhone SE
 * noch komfortabel tappable sind; gleichzeitig passen 2×2 auf einen Viewport
 * ohne Scroll (ausser bei sehr kleinen Geräten, aber dann scrollt der
 * Container sauber).
 */
function NaviTile({
  to,
  icon,
  title,
  description,
  disabled = false,
  pro = false,
  lodge = false,
}: {
  to?: string;
  icon: ReactNode;
  title: string;
  description: string;
  disabled?: boolean;
  /** Wave 14ca-fix26: rotes "PRO"-Schleifchen oben rechts. Markiert
      Features die später kostenpflichtig werden. */
  pro?: boolean;
  /** Wave 14ca-fix46: zusaetzlicher "Vertical Lodge"-Badge fuer Features
      die nur fuer Vertical-Lodge-Kunden verfuegbar sind. Wird unter dem
      Pro-Badge gestapelt wenn beide gesetzt sind. */
  lodge?: boolean;
}) {
  // Wave 14ca-fix40: Tiles flacher gemacht (User-Wunsch). min-h von 9.5rem auf
  // 7rem reduziert, Padding von p-4 auf p-3.5, Gap intern 3 → 2. Bleibt
  // tappable (44px+ Hoehe), spart aber sichtbar Platz und passt auf SE
  // mehr Tiles in den Viewport.
  const shared =
    'relative flex flex-col gap-2 rounded-2xl p-3.5 min-h-[7rem] border';

  if (disabled || !to) {
    return (
      <div
        className={`${shared} bg-turbo-700/20 border-turbo-700/40 opacity-50`}
        aria-disabled="true"
      >
        {pro && <ProBadge stackIndex={0} />}
        {lodge && <ProBadge label="Vertical Lodge" stackIndex={pro ? 1 : 0} />}
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-sky-500/10 text-sky-300">
          {icon}
        </span>
        <div className="mt-auto">
          <div className="text-white font-semibold text-[15px] leading-tight">
            {title}
          </div>
          <div className="text-white/45 text-xs mt-1">{description}</div>
        </div>
      </div>
    );
  }
  return (
    <Link
      to={to}
      className={`${shared} bg-turbo-700/30 border-sky-500/30 active:bg-sky-500/10 active:border-sky-500/60 transition-colors no-underline`}
    >
      {/* Wave 14cm-fix: Lodge-Badge auch im aktiven Pfad rendern — vorher
          war's NUR im disabled-Branch, also verschwand das "Vertical Lodge"-
          Badge sobald der "All Day Turbo"-Tile für VL-Kunden freigeschaltet
          wurde. */}
      {pro && <ProBadge stackIndex={0} />}
      {lodge && <ProBadge label="Vertical Lodge" stackIndex={pro ? 1 : 0} />}
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-sky-500/15 text-sky-300">
        {icon}
      </span>
      <div className="mt-auto">
        <div className="text-white font-semibold text-[15px] leading-tight">
          {title}
        </div>
        <div className="text-white/60 text-xs mt-1">{description}</div>
      </div>
    </Link>
  );
}

// --- Icons -----------------------------------------------------------------
// Line-Icons passend zum Rest der App (24×24 viewBox, stroke-width 2,
// currentColor). Farbgebung erfolgt durch den parent (sky-300).

function IconPen() {
  // Stift — symbolisiert das „Finger zeichnen". Klassisches Edit-Icon.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

function IconRoute() {
  // Route-Icon: zwei Punkte (Start/Ziel) verbunden durch eine geschwungene
  // Linie — symbolisiert die A→B-Berechnung. Passt zum IconRoute der TabBar.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <circle cx="6" cy="19" r="2.4" />
      <circle cx="18" cy="5" r="2.4" />
      <path d="M8.2 17.5c2-1.2 2.6-3 1.3-4.8-1.3-1.8-.7-3.6 1.3-4.8 1.4-.8 3-1 4.9-.9" />
    </svg>
  );
}

function IconCorridor() {
  // Korridor-Icon: eine Route mit parallelen "Puffer"-Linien links/rechts —
  // symbolisiert den Streifen um die Strecke, der als Offline-Paket kommt.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <path d="M12 21c-3-3.5 3-6.5 0-10-2.4-2.8 0-6 1.5-8" />
      <path d="M6.5 20c-2.2-3 2.2-6 0-9-1.8-2.4-.4-5 .8-7" opacity={0.45} />
      <path d="M17.5 22c-2.2-3 2.2-6 0-9-1.8-2.4-.4-5 .8-7" opacity={0.45} />
    </svg>
  );
}

function IconBookmark() {
  // Bookmark/Pin — symbolisiert eine gespeicherte Route. Ein klassisches
  // Lesezeichen-Shape ist sofort als "gesichert / abgelegt" lesbar und
  // grenzt sich visuell von den anderen Kacheln ab (die mehr Aktions-
  // orientiert sind: Trails-Liste, Stift, Shuttle).
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}
